import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslocoPipe } from '@jsverse/transloco';
import { ToasterComponent, ToastComponent, ToastHeaderComponent, ToastBodyComponent, ProgressComponent } from '@coreui/angular-pro';
import { NotificationService, NewVerificationNotification, GlobalToastMessage } from '../../../services/notification.service';

@Component({
    selector: 'app-global-notification',
    standalone: true,
    imports: [
        CommonModule,
        ToasterComponent,
        ToastComponent,
        ToastHeaderComponent,
        ToastBodyComponent,
        ProgressComponent,
        TranslocoPipe,
    ],
    template: `
        <c-toaster placement="top-end" class="p-3" position="fixed">
            <c-toast 
                *ngIf="showNotification()"
                (timer)="onToastTimerChange($event)"
                (visibleChange)="onToastVisibleChange($event)"
                [visible]="showNotification()"
                [autohide]="true"
                [delay]="5000"
                (click)="onNotificationClick()"
                style="cursor: pointer;">
                <c-toast-header [closeButton]="false">
                    {{ 'notifications.verification.title' | transloco }}
                </c-toast-header>
                <c-toast-body>
                    <p class="mb-2">{{ 'notifications.verification.message' | transloco: { refCode: notificationRefCode() } }}</p>
                    <c-progress thin [value]="toastPercentage()" />
                </c-toast-body>
            </c-toast>

            <c-toast
                *ngIf="showGlobalToast()"
                [visible]="showGlobalToast()"
                [autohide]="globalToastAutohide()"
                [delay]="globalToastDelay()"
                [color]="globalToastColor()"
                (visibleChange)="onGlobalToastVisibleChange($event)">
                <c-toast-header [closeButton]="true">
                    {{ globalToastTitle() || ('notifications.defaultTitle' | transloco) }}
                </c-toast-header>
                <c-toast-body>
                    {{ globalToastMessage() }}
                </c-toast-body>
            </c-toast>
        </c-toaster>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalNotificationComponent implements OnInit, OnDestroy {
    showNotification = signal(false);
    notificationRefCode = signal('');
    toastPercentage = signal(0);
    currentTicketId = signal<string | null>(null);
    showGlobalToast = signal(false);
    globalToastTitle = signal('');
    globalToastMessage = signal('');
    globalToastColor = signal('info');
    globalToastAutohide = signal(true);
    globalToastDelay = signal(5000);
    private destroy$ = new Subject<void>();

    constructor(
        private notificationService: NotificationService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.notificationService.getNewVerificationNotification$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((notification: NewVerificationNotification) => {
                this.displayNotification(notification);
            });

        this.notificationService.getGlobalToast$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((toast: GlobalToastMessage) => {
                this.displayGlobalToast(toast);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private displayNotification(notification: NewVerificationNotification): void {
        this.currentTicketId.set(notification.ticket_id);
        this.notificationRefCode.set(notification.ref_code);
        this.showNotification.set(true);
    }

    private displayGlobalToast(toast: GlobalToastMessage): void {
        this.globalToastTitle.set(toast.title || '');
        this.globalToastMessage.set(toast.message || '');
        this.globalToastColor.set(toast.color || 'info');
        this.globalToastAutohide.set(toast.autohide ?? true);
        this.globalToastDelay.set(toast.delay ?? 5000);
        this.showGlobalToast.set(true);
    }

    onNotificationClick(): void {
        const ticketId = this.currentTicketId();
        if (ticketId) {
            this.showNotification.set(false);
            this.router.navigate(['/risk/manual-verification', ticketId]);
        }
    }

    onToastVisibleChange(visible: boolean): void {
        this.showNotification.set(visible);
        this.toastPercentage.set(visible ? this.toastPercentage() : 0);
    }

    onToastTimerChange(value: number): void {
        this.toastPercentage.set(value * 25);
    }

    onGlobalToastVisibleChange(visible: boolean): void {
        this.showGlobalToast.set(visible);
    }
}
